/**
 * I conti dei soldi: fatturato, incassi, crediti aperti, costi, abbonamenti.
 *
 * Stavano dentro la pagina Analisi aziendali, dove non si potevano riusare.
 * Qui li usano sia la pagina sia i widget della dashboard, cosi' lo stesso
 * numero non puo' venire diverso in due posti.
 *
 * Le regole sono quelle di sempre:
 * - fatturato = imponibile emesso, per data fattura (competenza); le note di
 *   credito lo riducono; pro forma e DDT non contano;
 * - incassato = quota di imponibile delle rate pagate, per data di pagamento
 *   (cassa);
 * - crediti aperti = rate non pagate delle fatture (non delle note di
 *   credito), scadute se la scadenza e' prima di oggi;
 * - una rata o una fattura stornata (compensata da una nota di credito) non
 *   e' ne' incassata ne' aperta;
 * - costi = imponibile dei documenti dei fornitori, per data documento, cioe'
 *   senza IVA come il fatturato: prima si contava l'IVA, e costi e ricavi non
 *   erano confrontabili. Le note di credito dei fornitori li riducono, i
 *   documenti di trasporto non sono costi.
 */

import { getSubscriptionStatus } from '../subscriptionUtils';

export const MESI_BREVI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

/** Solo documenti fiscalmente rilevanti per il fatturato (proforma e DDT esclusi). */
export const TIPI_FISCALI = new Set(['invoice', 'invoice_with_delivery', 'credit_note']);

export interface RataFattura {
  amount: number | null;
  due_date: string | null;
  paid_at: string | null;
  payment_status: string | null;
  installment_number: number | null;
}

/** Le colonne della fattura che servono ai conti. */
export interface FatturaAnalisi {
  id: string;
  invoice_number: string | null;
  invoice_date: string;
  document_type: string;
  status: string;
  payment_status: string | null;
  payment_due_date: string | null;
  total_amount: number | null;
  taxable_amount: number | null;
  customers: { company_name: string | null } | null;
  invoice_installments: RataFattura[] | null;
}

export interface CostoAnalisi {
  total_amount: number | null;
  taxable_amount: number | null;
  /** expense, passive_credit_note, passive_delivery_note (da Fatture in Cloud). */
  document_type: string | null;
  document_date: string | null;
}

export interface AbbonamentoAnalisi {
  monthly_amount: number | null;
  status: string;
  next_renewal_date: string | null;
}

export interface ScadenzaAperta {
  fatturaId: string;
  numero: string | null;
  cliente: string;
  importo: number;
  scadenza: string | null;
  scaduta: boolean;
}

/** Anno -> dodici importi, uno per mese. */
export type ImportiPerAnno = Map<number, number[]>;

export interface AggregatiAnalisi {
  emessoPerAnno: ImportiPerAnno;
  incassatoPerAnno: ImportiPerAnno;
  costiPerAnno: ImportiPerAnno;
  /** Dal piu' recente; c'e' sempre l'anno in corso. */
  anniDisponibili: number[];
  /** In ordine di scadenza. */
  scadenzeAperte: ScadenzaAperta[];
  aperto: { scaduto: number; futuro: number; totale: number };
}

export const annoDi = (d: string | null | undefined): number | null => {
  if (!d) return null;
  const y = Number(String(d).slice(0, 4));
  return Number.isFinite(y) ? y : null;
};

/** Mese da 0 (gennaio) a 11. */
export const meseDi = (d: string | null | undefined): number | null => {
  if (!d) return null;
  const m = Number(String(d).slice(5, 7));
  return Number.isFinite(m) && m >= 1 && m <= 12 ? m - 1 : null;
};

/** Somma l'importo nel mese giusto; senza una data leggibile non conta. */
function aggiungi(mappa: ImportiPerAnno, data: string | null | undefined, importo: number): void {
  const anno = annoDi(data);
  const mese = meseDi(data);
  if (anno === null || mese === null) return;
  let mesi = mappa.get(anno);
  if (!mesi) {
    mesi = new Array(12).fill(0);
    mappa.set(anno, mesi);
  }
  mesi[mese] += importo;
}

/** I dodici importi di un anno, in una lista nuova (zeri se l'anno non c'e'). */
export function mesiAnno(mappa: ImportiPerAnno, anno: number): number[] {
  return [...(mappa.get(anno) ?? new Array(12).fill(0))];
}

/** Come pesa sui costi un documento di un fornitore. */
export function segnoCosto(tipo: string | null): 1 | -1 | 0 {
  if (tipo === 'passive_delivery_note') return 0;
  if (tipo === 'passive_credit_note') return -1;
  return 1;
}

/** Le rate della fattura; senza rate se ne ricava una sola dalla fattura. */
function rateDellaFattura(inv: FatturaAnalisi, totale: number): RataFattura[] {
  if (inv.invoice_installments && inv.invoice_installments.length > 0) {
    return inv.invoice_installments;
  }
  const stato = inv.payment_status === 'paid' || inv.payment_status === 'reversed' ? inv.payment_status : 'pending';
  return [{
    amount: totale,
    due_date: inv.payment_due_date || inv.invoice_date,
    paid_at: null,
    payment_status: stato,
    installment_number: 1,
  }];
}

export function aggregaFatture(
  fatture: readonly FatturaAnalisi[],
  costi: readonly CostoAnalisi[],
  { oggi, annoCorrente }: { oggi: string; annoCorrente: number },
): AggregatiAnalisi {
  const emessoPerAnno: ImportiPerAnno = new Map();
  const incassatoPerAnno: ImportiPerAnno = new Map();
  const costiPerAnno: ImportiPerAnno = new Map();
  const scadenzeAperte: ScadenzaAperta[] = [];
  let scaduto = 0;
  let futuro = 0;

  for (const inv of fatture) {
    if (!TIPI_FISCALI.has(inv.document_type)) continue;
    const segno = inv.document_type === 'credit_note' ? -1 : 1;
    const totale = inv.total_amount || 0;
    const imponibile = (inv.taxable_amount || 0) * segno;

    aggiungi(emessoPerAnno, inv.invoice_date, imponibile);

    for (const r of rateDellaFattura(inv, totale)) {
      if (r.payment_status === 'reversed') continue;
      const importoRata = (r.amount || 0) * segno;
      // La quota di imponibile corrispondente a questa rata.
      const quotaImponibile = totale > 0 ? imponibile * ((r.amount || 0) / totale) : 0;

      if (r.payment_status === 'paid') {
        aggiungi(incassatoPerAnno, r.paid_at || r.due_date || inv.invoice_date, quotaImponibile);
      } else if (segno > 0) {
        // Solo le fatture, non le note di credito, generano crediti da incassare.
        const scaduta = !!r.due_date && r.due_date < oggi;
        if (scaduta) scaduto += importoRata;
        else futuro += importoRata;
        scadenzeAperte.push({
          fatturaId: inv.id,
          numero: inv.invoice_number,
          cliente: inv.customers?.company_name || '—',
          importo: importoRata,
          scadenza: r.due_date,
          scaduta,
        });
      }
    }
  }

  for (const c of costi) {
    const segno = segnoCosto(c.document_type);
    if (segno === 0) continue;
    // Senza imponibile si ripiega sul totale: meglio un costo un po' alto
    // che un costo sparito.
    aggiungi(costiPerAnno, c.document_date, (c.taxable_amount ?? c.total_amount ?? 0) * segno);
  }

  const anni = new Set<number>([annoCorrente]);
  [emessoPerAnno, incassatoPerAnno, costiPerAnno].forEach((m) => m.forEach((_v, anno) => anni.add(anno)));

  return {
    emessoPerAnno,
    incassatoPerAnno,
    costiPerAnno,
    anniDisponibili: [...anni].sort((a, b) => b - a),
    scadenzeAperte: [...scadenzeAperte].sort((a, b) => (a.scadenza || '').localeCompare(b.scadenza || '')),
    aperto: { scaduto, futuro, totale: scaduto + futuro },
  };
}

export interface StatisticheAbbonamenti {
  attivi: number;
  /** Ricavo ricorrente mensile. */
  mrr: number;
  /** Annuo: mensile per dodici. */
  arr: number;
  /** Attivi con rinnovo entro 30 giorni. */
  inScadenza: number;
}

/** Ricavo ricorrente degli abbonamenti attivi: informativo, non si somma al fatturato. */
export function statisticheAbbonamenti(
  righe: readonly AbbonamentoAnalisi[],
  oggi?: string,
): StatisticheAbbonamenti {
  const attivi = righe.filter((s) => s.status === 'active');
  const mrr = attivi.reduce((somma, s) => somma + (s.monthly_amount || 0), 0);
  const inScadenza = attivi.filter(
    (s) => s.next_renewal_date && getSubscriptionStatus(s.next_renewal_date, oggi) === 'warning',
  ).length;
  return { attivi: attivi.length, mrr, arr: mrr * 12, inScadenza };
}
